/**
 * The DOM HUD overlay — a sibling of <Canvas> (NOT a child). The container is
 * `pointer-events:none` so clicks fall through to the Three.js raycaster
 * (clickable dice/cards); each interactive widget opts back IN with
 * `pointer-events:auto` (handled in index.css via `.hud > *` / `.hud-widget`).
 *
 * This component is the phase router for the 2D UI: it decides which panels show
 * for the current join state + game phase. It holds no authoritative state — it
 * reads the store (a projection of server messages) and sends intents.
 */

import { useGameStore } from '../state/store';
import { IdCard } from './IdCard';
import { Roster } from './Roster';
import { BettingControls } from './BettingControls';
import { ConfigPanel } from './ConfigPanel';
import { LeaderboardA11y } from './LeaderboardSign';
import { Countdown321 } from './Countdown321';
import { DecisionTimer } from './DecisionTimer';
import { ActionBar } from './ActionBar';
import { Announcement } from './Announcement';
import { WinnersLosersGrid } from './WinnersLosersGrid';
import { HostChoice } from './HostChoice';
import { ConnectionBadge } from './ConnectionBadge';
import { DealerPanel } from './HandView';
import { TiebreakDirector, TiebreakPanels } from './Tiebreak';
import { SfxCues } from './SfxCues';

export function Hud() {
  const joined = useGameStore((s) => s.joined);
  const phase = useGameStore((s) => s.server.phase);

  const inLobby = phase === 'LOBBY';
  const inBetting = phase === 'BETTING';
  const inResults = phase === 'RESULT' || phase === 'RESOLUTION';

  return (
    <div className="hud">
      {/* Title marquee. The High Rollers leaderboard now lives as a 3D backlit
          sign in the scene (HighRollersSign.tsx); only a visually-hidden a11y
          mirror of the standings remains in the DOM. */}
      <h1 className="title title-gold">Blackjack Standup Picker</h1>
      <LeaderboardA11y />

      <div className="hud-widget conn-slot">
        <ConnectionBadge />
      </div>

      {/* Entry gate: ID card until joined. */}
      {!joined && (
        <div className="hud-widget center-slot">
          <IdCard />
        </div>
      )}

      {joined && (
        <>
          {/* Roster is always visible (names always on, per accessibility brief). */}
          <div className="hud-widget left-slot">
            <Roster />
          </div>

          {/* Lobby: host config + (for players) bets. */}
          {(inLobby || inBetting) && (
            <div className="hud-widget right-slot">
              <ConfigPanel />
              {inBetting && <BettingControls />}
            </div>
          )}

          {/* Dealer hand + score (readable mirror of the felt). */}
          <div className="hud-widget dealer-slot">
            <DealerPanel />
          </div>

          {/* PLAYER_ACTIONS: per-decision timer ring + hit/stand. */}
          <div className="hud-widget timer-slot">
            <DecisionTimer />
          </div>
          <div className="hud-widget bottom-slot">
            <ActionBar />
          </div>

          {/* Resolution / result. */}
          {inResults && (
            <div className="hud-widget right-slot">
              <WinnersLosersGrid />
              <HostChoice />
            </div>
          )}
        </>
      )}

      {/* Overlays that float above everything (their own slots in CSS). */}
      <Countdown321 />
      <Announcement />
      {/* Paced dice-tiebreak sequence (director drives beats; panels render). */}
      {joined && <TiebreakDirector />}
      <TiebreakPanels />
      {/* Phase/beat-driven sound cues (deal, dice rattle). */}
      {joined && <SfxCues />}
    </div>
  );
}
